import { parseCron } from '../types/program';
import { getTodayDateStringInBusinessTZ, toISODateLocal } from './date';

const LA_TIMEZONE = 'America/Los_Angeles';
export const CHECKIN_BEFORE_MIN = 60;
export const CHECKIN_AFTER_MIN = 30;

function getNowInLA(): Date {
  const now = new Date();
  // toLocaleString in LA timezone gives a wall-clock string we can re-parse
  const laStr = now.toLocaleString('en-US', { timeZone: LA_TIMEZONE });
  return new Date(laStr);
}

/**
 * Returns today's session start time as a Date (LA wall-clock) for the given
 * schedule cron, or null if there is no session today.
 */
export function getTodaySessionTime(scheduleCron: string): Date | null {
  const { minute, hour, days } = parseCron(scheduleCron);
  const nowLA = getNowInLA();
  const dowLA = nowLA.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat

  if (!days.includes(dowLA)) return null;

  const session = new Date(nowLA);
  session.setHours(hour, minute, 0, 0);
  return session;
}

/** True if the program's cron schedule has a session today (LA timezone). */
export function hasSessionToday(scheduleCron: string): boolean {
  return getTodaySessionTime(scheduleCron) !== null;
}

/**
 * True if the parent check-in window is open:
 * [session_start - CHECKIN_BEFORE_MIN, session_start + CHECKIN_AFTER_MIN].
 */
export function isCheckInWindowOpen(scheduleCron: string): boolean {
  const sessionTime = getTodaySessionTime(scheduleCron);
  if (!sessionTime) return false;

  const nowLA = getNowInLA();
  const windowStart = new Date(sessionTime.getTime() - CHECKIN_BEFORE_MIN * 60 * 1000);
  const windowEnd = new Date(sessionTime.getTime() + CHECKIN_AFTER_MIN * 60 * 1000);

  return nowLA >= windowStart && nowLA <= windowEnd;
}

/**
 * Returns today's ISO date string (YYYY-MM-DD) in LA timezone.
 * Alias of the shared date utility, re-exported here for co-location with schedule logic.
 */
export { getTodayDateStringInBusinessTZ as getTodaySessionDate };

/**
 * The weekdays a program meets, as JS `Date.getDay()` numbers (0=Sunday … 6=Saturday).
 *
 * Normalizes the two cron conventions `parseCron` leaves raw: cron accepts both `0` and `7`
 * for Sunday, and `*` expands to `1..7` there — which drops Sunday when compared against
 * `getDay()`. Taking everything mod 7 folds `7` back onto `0` and makes both correct.
 */
export function getSessionWeekdays(scheduleCron: string): Set<number> {
  return new Set(parseCron(scheduleCron).days.map(d => d % 7));
}

/**
 * Weekday (0=Sunday … 6=Saturday) of a `YYYY-MM-DD` calendar day.
 *
 * Built from the date parts as a LOCAL date, so it never shifts the way
 * `new Date('2026-09-02')` (parsed as UTC midnight) does west of Greenwich.
 */
export function weekdayOfDateString(ymd: string): number {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
}

/** `YYYY-MM-DD` shifted by `deltaDays`, rolling across month/year boundaries. */
export function shiftDateString(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return toISODateLocal(new Date(y, m - 1, d + deltaDays));
}

/**
 * True if the student's selected_days array (per_day programs) includes today.
 */
export function isStudentScheduledToday(selectedDays: string[] | null | undefined): boolean {
  if (!selectedDays || selectedDays.length === 0) return false;
  const today = getTodayDateStringInBusinessTZ();
  return selectedDays.includes(today);
}

const MINUTES_PER_DAY = 24 * 60;

/** Minutes past midnight as wall-clock text — `870` → `2:30 PM`. */
function formatClockTime(minutesOfDay: number): string {
  const hour24 = Math.floor(minutesOfDay / 60);
  const minute = minutesOfDay % 60;
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${minute.toString().padStart(2, '0')} ${hour24 < 12 ? 'AM' : 'PM'}`;
}

/**
 * When a class meets, as the teacher would say it: `2:30 PM – 3:15 PM`.
 *
 * The start comes from the cron's minute and hour fields, the end from `duration` — the
 * only place BHCS records when a class finishes. Both are stored as business-timezone
 * wall-clock, and this returns wall-clock, so no instant is ever constructed and there is
 * nothing here for a device timezone to shift.
 *
 * Falls back to the start alone when `duration` is missing or nonsensical, and to `null`
 * when the cron itself has no readable time — a half-answer beats a wrong one, and no
 * answer beats a made-up one.
 *
 * Only cron's first two fields are read. Which *days* the program meets is a separate
 * question, answered by `getSessionWeekdays`, and callers already know the class meets
 * today or they would not be looking at its roster.
 */
export function formatSessionTimeRange(
  scheduleCron: string | null | undefined,
  durationMinutes: number | null | undefined,
): string | null {
  const fields = (scheduleCron ?? '').trim().split(/\s+/);
  if (fields.length !== 5) return null;

  const minute = Number(fields[0]);
  const hour = Number(fields[1]);
  if (!Number.isInteger(minute) || !Number.isInteger(hour)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

  const start = hour * 60 + minute;
  const duration = Number(durationMinutes);
  // A class that would run past midnight is bad data rather than an overnight session, so
  // the end time is dropped instead of being printed as an early-morning hour.
  if (!Number.isFinite(duration) || duration <= 0 || start + duration >= MINUTES_PER_DAY) {
    return formatClockTime(start);
  }

  return `${formatClockTime(start)} – ${formatClockTime(start + duration)}`;
}

/**
 * Minutes past midnight on the business-timezone wall clock, right now.
 *
 * The counterpart to `formatSessionTimeRange`'s output: both sides of "has this class
 * started yet" are then plain minute counts on the same clock, with no instant constructed
 * and nothing for a device timezone to shift. A parent in New York asking at 7pm their time
 * gets the same answer as the office does at 4pm.
 */
export function currentBusinessMinutesOfDay(now: Date = new Date()): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: LA_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const read = (type: 'hour' | 'minute') => Number(parts.find(p => p.type === type)?.value ?? '0');
  // Intl renders midnight as hour 24 in some engines under hour12:false.
  return (read('hour') % 24) * 60 + read('minute');
}

/**
 * The cron's start time as minutes past midnight, or null when it has no readable one.
 *
 * Shares `formatSessionTimeRange`'s validation deliberately: anything that helper refuses to
 * print a time for is also something this must not sort by, or a malformed cron would quietly
 * claim the 12:00 AM slot at the top of a parent's day.
 */
export function sessionStartMinutes(scheduleCron: string | null | undefined): number | null {
  const fields = (scheduleCron ?? '').trim().split(/\s+/);
  if (fields.length !== 5) return null;
  const minute = Number(fields[0]);
  const hour = Number(fields[1]);
  if (!Number.isInteger(minute) || !Number.isInteger(hour)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
}

/**
 * `12:00 PM – 6:00 PM` → `12:00 – 6:00 PM`.
 *
 * Both halves of a session almost always share a meridiem, and printing it twice is the
 * widest thing in the week calendar's time column — space a seven-day grid does not have to
 * spare. Anything this does not recognise (a start-only label, a session that crosses noon or
 * midnight) is returned untouched, so no label can lose information it was carrying.
 */
export function compactTimeRange(label: string): string {
  const match = /^(\d{1,2}:\d{2}) (AM|PM) – (\d{1,2}:\d{2}) (AM|PM)$/.exec(label);
  if (!match) return label;
  const [, start, startMeridiem, end, endMeridiem] = match;
  return startMeridiem === endMeridiem ? `${start} – ${end} ${endMeridiem}` : label;
}
